import React from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button } from '@material-ui/core';

import { StoryCard, MapCard, CardDb, isMapCard, updateCard, deleteCard } from '../redux/cardDbSlice';
import { deleteCardAttribute } from '../redux/cardAttributeDbSlice';
import { deleteCardShape } from '../redux/cardShapeDbSlice';
import { deleteMemoCardContent } from '../redux/memoCardContentDbSlice';
import { deleteUiCardItem } from '../redux/uiCardItemDbSlice';
import { deleteDrawCardContent } from '../redux/drawCardContentDbSlice';
import { deleteArrow } from '../redux/arrowDbSlice';
import { deleteNote } from '../redux/noteDbSlice';
import useOneCardDb from '../hooks/useOneCardDb';
import useCardAttributeDbs from '../hooks/useCardAttributeDbs';
import useCardShapeDbs from '../hooks/useCardShapeDbs';
import useMemoCardContentDb from '../hooks/useMemoCardContentDb';
import useUiCardItemDbs from '../hooks/useUiCardItemDbs';
import useDrawCardContentDb from '../hooks/useDrawCardContentDb';
import useChildrenCardIdsOfStoryCard from '../hooks/useChildrenCardIdsOfStoryCard';
import useCardAttributeIdsByCards from '../hooks/useCardAttributeIdsByCards';
import useCardShapeIdsByCards from '../hooks/useCardShapeIdsByCards';
import useMemoCardContentIdsByCards from '../hooks/useMemoCardContentIdsByCards';
import useUiCardItemIdsByCards from '../hooks/useUiCardItemIdsByCards';
import useDrawCardContentIdsByCards from '../hooks/useDrawCardContentIdsByCards';
import useArrowDbsByCard from '../hooks/useArrowDbsByCard';
import useNoteDbsByReferences from '../hooks/useNoteDbsByReferences';

type DeleteCardDialogProps = {
  open: boolean;
  onClose: () => void;
  id?: string;
  sheetId?: string;
  ids: string[];
  cards: CardDb[];
  index?: number;
  setStoryIndex: (ix?: number) => void;
  putDownForwardCard: (isGroup: boolean, groupParent: string | undefined, z: number) => void;
  operateGroupChildren: (parentId: string, parent: MapCard, method: 'takeoff' | 'delete', foregroundZ?: number) => void;
}
const DeleteCardDialog: React.FC<DeleteCardDialogProps> = ({open, onClose, id, sheetId, ids, cards, index, setStoryIndex, putDownForwardCard, operateGroupChildren}) => {
  const dispatch = useDispatch();
  const cardValue = useOneCardDb(id);
  const [attributeIds] = useCardAttributeDbs(id);
  const [shapeIds] = useCardShapeDbs(id);
  const [memoContentId] = useMemoCardContentDb(id);
  const [uiItemIds] = useUiCardItemDbs(id);
  const [drawContentId] = useDrawCardContentDb(id);
  const childrenIds = useChildrenCardIdsOfStoryCard(id);
  const childrenAttributeIds = useCardAttributeIdsByCards(childrenIds);
  const childrenShapeIds = useCardShapeIdsByCards(childrenIds);
  const childrenMemoContentIds = useMemoCardContentIdsByCards(childrenIds);
  const childrenUiItemIds = useUiCardItemIdsByCards(childrenIds);
  const childrenDrawContentIds = useDrawCardContentIdsByCards(childrenIds);
  const [arrowIds, arrows] = useArrowDbsByCard(id);
  const [noteIds] = useNoteDbsByReferences([id]);

  if (!cardValue) return <></>;

  const deleteExe = (id: string) => {
    dispatch(deleteCard({ id: id }));
    // delete attributes
    if (attributeIds) attributeIds.forEach(targetId => dispatch(deleteCardAttribute({ id: targetId })));
    // delete shapes
    if (shapeIds) shapeIds.forEach(targetId => dispatch(deleteCardShape({ id: targetId })));
    // delete memo card content
    if (memoContentId) dispatch(deleteMemoCardContent({ id: memoContentId }));
    // delete ui card items
    if (uiItemIds) uiItemIds.forEach(targetId => dispatch(deleteUiCardItem({ id: targetId })));
    // delete draw card content
    if (drawContentId) dispatch(deleteDrawCardContent({ id: drawContentId }));
    // ストーリーの並び順を更新
    if (index !== undefined && cardValue.sheetKind === 'story') {
      if (ids.length >= 2) setStoryIndex(index === 0 ? 0 : index - 1);
      else setStoryIndex(undefined);
      ids.forEach((id, ix) => {
        const card = cards[ix] as StoryCard;
        if (card.parentId === cardValue.parentId && card.order > cardValue.order) dispatch(updateCard({ id, order: card.order - 1 }));
      })
    }
    // update z property of cards in map
    if (isMapCard(cardValue)) {
      putDownForwardCard(cardValue.isGroup, cardValue.groupParent, cardValue.z);
      operateGroupChildren(id, cardValue, 'delete');
    }
    // delete children cards
    if (childrenIds) childrenIds.forEach(targetId => dispatch(deleteCard({ id: targetId })));
    // delete children attributes
    if (childrenAttributeIds) childrenAttributeIds.forEach(targetId => dispatch(deleteCardAttribute({ id: targetId })));
    // delete children shapes
    if (childrenShapeIds) childrenShapeIds.forEach(targetId => dispatch(deleteCardShape({ id: targetId })));
    // delete children memo card contents
    if (childrenMemoContentIds) childrenMemoContentIds.forEach(targetId => dispatch(deleteMemoCardContent({ id: targetId })));
    // delete children ui card items
    if (childrenUiItemIds) childrenUiItemIds.forEach(targetId => dispatch(deleteUiCardItem({ id: targetId })));
    // delete children draw card contents
    if (childrenDrawContentIds) childrenDrawContentIds.forEach(targetId => dispatch(deleteDrawCardContent({ id: targetId })));
    // delete from or to arrows
    arrows.forEach((arrow, index) => {
      if (arrow.fromId === id || arrow.toId === id) {
        dispatch(deleteArrow({id: arrowIds[index]}));
      }
    })
    // delete notes
    noteIds.forEach(targetId => dispatch(deleteNote({ id: targetId })))
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>カードを削除します</DialogTitle>
      <DialogContent />
      <DialogActions>
        <Button variant='text' color='primary' onClick={onClose}>
          キャンセル
        </Button>
        <Button variant='text' color='primary' onClick={() => {if (id) deleteExe(id)}}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteCardDialog;
